import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
// TODO: Replace with svg
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { DataTable, type TableColumn } from '@/src/ui';
import type { StudentStandardsSummaryDTO } from '../models/standardsReportDTOs';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';

interface StandardsStudentTableProps {
    data: StudentStandardsSummaryDTO[];
    emptyMessage?: string;
    useSeed?: boolean;
}

const StandardsStudentTable: React.FC<StandardsStudentTableProps> = ({
    data,
    emptyMessage,
    useSeed,
}) => {
    const [openMap, setOpenMap] = useState<Record<number, boolean>>({});
    const toggle = (id: number) => setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

    const rows = useMemo(() => {
        const out: any[] = [];
        for (const s of data ?? []) {
            const standardsLite = (s.standards ?? []).map((st) => ({
                standardId: st.standardId,
                standardCode: st.standardCode,
            }));

            out.push({
                isHeader: true,
                studentId: s.studentId,
                studentName: s.studentName,
                standardsLite,
            });

            if (openMap[s.studentId]) {
                // Already grouped by backend: each item is a parent row with optional subs inline
                const sorted = [...(s.standards ?? [])].sort((a, b) =>
                    (a.standardCode || '').localeCompare(b.standardCode || '', undefined, {
                        sensitivity: 'base',
                        numeric: true,
                    })
                );

                for (const st of sorted) {
                    out.push({
                        isHeader: false,
                        studentId: s.studentId,
                        studentName: s.studentName,
                        standardId: st.standardId,
                        standardCode: st.standardCode,
                        subs: Array.isArray(st.substandardCodes) ? st.substandardCodes : [],
                        skillDescription: st.skillDescription,
                        displayCorrect: st.questionsCorrect,
                        displayTotal: st.questionsTotal,
                        displayPercent: st.percentCorrect,
                        classPercentCorrect: st.classPercentCorrect,
                    });
                }
            }
        }
        return out;
    }, [data, openMap]);

    const columns: TableColumn[] = [
        {
            dataKey: 'studentName',
            header: 'Student',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                if (row.isHeader) {
                    const standardsLite = row.standardsLite ?? [];
                    const standardIds = standardsLite
                        .map((st: { standardId: number }) => st.standardId)
                        .filter((id: number) => !!id);

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                size="small"
                                aria-label={openMap[row.studentId] ? 'Collapse' : 'Expand'}
                                onClick={() => toggle(row.studentId)}
                                sx={{
                                    p: 0.25,
                                    color: 'inherit',
                                    '&:focus': { outline: 'none' },
                                    '&:focus-visible': { outline: 'none' },
                                    '&:hover': { backgroundColor: 'transparent', color: 'black' },
                                }}
                            >
                                {openMap[row.studentId] ? (
                                    <KeyboardArrowUp />
                                ) : (
                                    <KeyboardArrowDown />
                                )}
                            </IconButton>

                            <Link
                                to={`/reports/standardsStudentDetails?studentId=${row.studentId}${
                                    useSeed ? '&seed=1' : ''
                                }&standardIds=${standardIds.join(',')}`}
                                state={{ standards: standardsLite }}
                                title="View student mastery"
                                style={{ fontWeight: 600 }}
                            >
                                {row.studentName || 'Unknown'}
                            </Link>
                        </Box>
                    );
                }
                return '';
            },
        },
        {
            dataKey: 'standardCode',
            header: 'Standard',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) =>
                row.isHeader ? (
                    ''
                ) : (
                    <span
                        style={{ whiteSpace: 'nowrap', display: 'inline-block', lineHeight: 1.4 }}
                    >
                        {row.standardCode || ''}
                        {Array.isArray(row.subs) && row.subs.length > 0 && (
                            <span style={{ opacity: 0.9 }}>
                                {', '}
                                {row.subs.map((c: string, i: number) => (
                                    <React.Fragment key={i}>
                                        {c}
                                        {i < row.subs.length - 1 ? ', ' : ''}
                                    </React.Fragment>
                                ))}
                            </span>
                        )}
                    </span>
                ),
        },
        {
            dataKey: 'skillDescription',
            header: 'Skill / Description',
            allowSort: false,
            align: 'left',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : row.skillDescription || ''),
        },
        {
            dataKey: 'questionsCorrect',
            header: 'Correct',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : `${row.displayCorrect ?? 0}`),
        },
        {
            dataKey: 'questionsTotal',
            header: 'Total',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : `${row.displayTotal ?? 0}`),
        },
        {
            dataKey: 'percentCorrect',
            header: 'Student %',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : `${row.displayPercent ?? 0}%`),
        },
        {
            dataKey: 'classPercentCorrect',
            header: 'Class %',
            allowSort: false,
            align: 'center',
            emptyValue: '',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => (row.isHeader ? '' : `${row.classPercentCorrect ?? 0}%`),
        },
    ];

    return (
        <div className="w-full overflow-auto">
            <br />

            <div className="pl-4">
                <h2
                    className="inline-block text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide whitespace-nowrap"
                    style={{
                        fontFamily: 'Verdana, Geneva, sans-serif',
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                    }}
                >
                    Student Skill Summary
                </h2>
            </div>

            <DataTable
                data={rows}
                columns={columns}
                tableName="StandardsStudentTable"
                emptyMessage={emptyMessage}
                rowsPerPageInitial={50}
                enableExportCSV
                alternatingRowColor
                defaultSortColumn="studentName"
                defaultSortDirection="asc"
            />
        </div>
    );
};

export default StandardsStudentTable;
